import type { CSSProperties } from 'react'

import { HeroKicker } from '@/components/marketing/hero-kicker'
import { MarketingHeroLead } from '@/components/marketing/marketing-hero-lead'
import { HeroTitle } from '@/components/marketing/hero-title'
import { Button } from '@/components/ui/button'
import { cn } from '@/lib/utils'

export function MarketingHero({
  eyebrow,
  title,
  lead,
  paragraphs,
  ctaLabel,
  ctaHref,
  heroImage,
  heroImageMaxWidth,
  children,
  className,
}: {
  eyebrow: string
  title: string
  lead?: string
  paragraphs?: string[]
  ctaLabel?: string
  ctaHref?: string
  heroImage?: string
  heroImageMaxWidth?: number
  children?: React.ReactNode
  className?: string
}) {
  return (
    <section
      className={cn(
        'marketing-hero relative overflow-visible',
        heroImage && 'marketing-hero--with-diagram',
        className
      )}
    >
      <div
        className={cn(
          'marketing-container relative flex flex-col items-center overflow-visible text-center',
          children ? 'justify-start pt-20 pb-0 lg:pt-28' : 'justify-center py-20 lg:py-28'
        )}
      >
        <div className="hero-stack">
          <HeroKicker>{eyebrow}</HeroKicker>
          <HeroTitle className="max-w-4xl">{title}</HeroTitle>
          {lead ? <MarketingHeroLead>{lead}</MarketingHeroLead> : null}
          {paragraphs?.length ? (
            <div className="hero-stack__paragraphs">
              {paragraphs.map((paragraph) => (
                <MarketingHeroLead key={paragraph}>{paragraph}</MarketingHeroLead>
              ))}
            </div>
          ) : null}
          {ctaLabel && ctaHref ? (
            <Button asChild variant="cta" size="cta">
              <a href={ctaHref} className="hero-stack__cta marketing-cta__button">
                {ctaLabel}
              </a>
            </Button>
          ) : null}
        </div>
        {children}
        {heroImage ? (
          <div
            className="marketing-hero__diagram"
            style={
              heroImageMaxWidth
                ? ({
                    '--marketing-hero-diagram-max-width': `${heroImageMaxWidth}px`,
                  } as CSSProperties)
                : undefined
            }
          >
            <img
              src={heroImage}
              alt=""
              width={1046}
              height={352}
              loading="lazy"
              decoding="async"
            />
          </div>
        ) : null}
      </div>
    </section>
  )
}
